import type { DraftShelfLifeRuleForReview, FoodType, KitchenUseSignal, StorageState } from "./types";

export const REVIEW_STORAGE_STATES: readonly StorageState[] = ["chilled", "frozen", "defrosted", "ambient"];

// A department can hold thousands of drafts; rendering them all at once makes
// the list unusable on a kitchen tablet, so the page reveals them in steps.
export const REVIEW_PAGE_SIZE = 100;

export type ReviewStorageFilter = StorageState | "all";

/** Food types in the order a kitchen thinks of them; matches packages.domain.catalogue.FOOD_TYPES. */
export const FOOD_TYPE_LABELS: ReadonlyArray<readonly [FoodType, string]> = [
  ["meat", "Meat and poultry"],
  ["fish", "Fish and seafood"],
  ["dairy", "Dairy and eggs"],
  ["cheese", "Cheese"],
  ["deli", "Deli and prepared"],
  ["produce", "Fruit, veg and herbs"],
  ["frozen", "Bought frozen"],
  ["frozen_produce", "Frozen veg and fruit"],
  ["sauces_jars", "Sauces, jars and UHT"],
  ["dry_goods", "Dry goods"],
];

/** "none" is products with no type: the chefs' own items and anything not from a supplier build. */
export type ReviewTypeFilter = FoodType | "none" | "all";

function typeOf(rule: DraftShelfLifeRuleForReview): FoodType | "none" {
  return rule.food_type ?? "none";
}

/**
 * "in-use" narrows the list to products with evidence the kitchen labels them
 * (they were in WPF, sit on a prep list or buffet menu, or have been printed).
 * The supplier catalogue holds far more than the kitchen ever date-labels, so
 * this is the short list worth deciding before a pilot.
 */
export type ReviewScope = "in-use" | "all";

const KITCHEN_USE_LABELS: Record<KitchenUseSignal, string> = {
  wpf: "was in WPF",
  prep_list: "on a prep list",
  buffet: "on a buffet menu",
  printed: "printed in Kitchen Hub",
};

export function isInKitchenUse(rule: DraftShelfLifeRuleForReview): boolean {
  return (rule.kitchen_use?.length ?? 0) > 0;
}

export function kitchenUseLabel(rule: DraftShelfLifeRuleForReview): string | null {
  if (!isInKitchenUse(rule)) return null;
  const reasons = rule.kitchen_use!.map((signal) => KITCHEN_USE_LABELS[signal]);
  return `Kitchen uses this: ${reasons.join(", ")}`;
}

export function filterReviewRules(
  rules: readonly DraftShelfLifeRuleForReview[],
  search: string,
  storageState: ReviewStorageFilter,
  scope: ReviewScope = "all",
  foodType: ReviewTypeFilter = "all",
): DraftShelfLifeRuleForReview[] {
  const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const matching = rules.filter((rule) => {
    if (scope === "in-use" && !isInKitchenUse(rule)) return false;
    if (foodType !== "all" && typeOf(rule) !== foodType) return false;
    if (storageState !== "all" && rule.storage_state !== storageState) return false;
    const name = rule.product_name.toLowerCase();
    return terms.every((term) => name.includes(term));
  });
  // Products the kitchen uses come first even in the full list; otherwise the
  // server's alphabetical order is kept (sort is stable).
  return matching.sort((a, b) => Number(isInKitchenUse(b)) - Number(isInKitchenUse(a)));
}

export function countByStorageState(
  rules: readonly DraftShelfLifeRuleForReview[],
): Record<StorageState, number> {
  const counts: Record<StorageState, number> = { chilled: 0, frozen: 0, defrosted: 0, ambient: 0 };
  for (const rule of rules) counts[rule.storage_state] += 1;
  return counts;
}

/**
 * Every loaded draft for each product, in storage-state order, whatever the
 * current search or storage filter shows -- "Approve product" decides them all,
 * and its confirmation lists each one so none goes through unseen.
 */
export function draftsByProduct(
  rules: readonly DraftShelfLifeRuleForReview[],
): Map<string, DraftShelfLifeRuleForReview[]> {
  const byProduct = new Map<string, DraftShelfLifeRuleForReview[]>();
  for (const rule of rules) {
    const drafts = byProduct.get(rule.product_id);
    if (drafts) drafts.push(rule);
    else byProduct.set(rule.product_id, [rule]);
  }
  for (const drafts of byProduct.values()) {
    drafts.sort((a, b) => REVIEW_STORAGE_STATES.indexOf(a.storage_state) - REVIEW_STORAGE_STATES.indexOf(b.storage_state));
  }
  return byProduct;
}

/** Products (not rules) of each type, so the filter reads like the kitchen's stock list. */
export function countProductsByType(
  rules: readonly DraftShelfLifeRuleForReview[],
): Map<FoodType | "none", number> {
  const products = new Map<FoodType | "none", Set<string>>();
  for (const rule of rules) {
    const kind = typeOf(rule);
    if (!products.has(kind)) products.set(kind, new Set());
    products.get(kind)!.add(rule.product_id);
  }
  return new Map([...products].map(([kind, ids]) => [kind, ids.size]));
}

export function countInKitchenUse(rules: readonly DraftShelfLifeRuleForReview[]): { rules: number; products: number } {
  const inUse = rules.filter(isInKitchenUse);
  return { rules: inUse.length, products: new Set(inUse.map((rule) => rule.product_id)).size };
}
